import { LoadingOverlay } from '@mantine/core';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ProjectForm } from 'src/Components/Forms/Project/ProjectForm';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { EMPTY_DESCRIPTION, ProjectFormValues } from 'src/Hooks/Forms/useCreateProjectForm';
import useDeleteProject from 'src/Hooks/Mutations/useDeleteProject';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import useUpdateProject from 'src/Hooks/Mutations/useUpdateProject';
import useFetchProjects from 'src/Hooks/Queries/useFetchProjects';
import {
  AssetStorageItemDtoTypeEnum,
  CreateProjectDtoEstimatedDurationUnitEnum,
  CreateProjectDtoStatusEnum,
  ProjectDto,
  UpdateProjectDto,
} from 'src/Services/API/generated/api';

import { Props } from './ProjectEditionFormWrapper.types';

const parseDescription = (description: string | null | undefined): SlateNode[] => {
  if (!description) return EMPTY_DESCRIPTION;
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) return parsed;
    return EMPTY_DESCRIPTION;
  } catch {
    // Legacy plain text - wrap in paragraph
    return [{ type: 'paragraph', children: [{ text: description }] }];
  }
};

const isDescriptionEmpty = (description: SlateNode[]) => {
  if (!description || description.length === 0) return true;
  return JSON.stringify(description) === JSON.stringify(EMPTY_DESCRIPTION);
};

const ProjectEditionFormWrapper = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { assetId, projectId } = useParams<{ assetId: string; projectId: string }>();
  const { data: projects, isLoading: isLoadingProjects } = useFetchProjects(assetId!);
  const { mutate: updateProject, isLoading: isUpdating } = useUpdateProject(assetId!);
  const { mutate: deleteProject, isLoading: isDeleting } = useDeleteProject(assetId!);

  const project = projects?.find(p => p.id === projectId);

  const form = useForm<ProjectFormValues>({
    initialValues: {
      title: '',
      description: EMPTY_DESCRIPTION,
      status: CreateProjectDtoStatusEnum.IdeaBox,
      estimatedCost: undefined,
      estimatedDuration: undefined,
      estimatedDurationUnit: CreateProjectDtoEstimatedDurationUnitEnum.Days,
      deadline: null,
      completionDate: null,
      label: null,
      priority: null,
      memberIds: [],
      participantIds: [],
      attachments: [],
    },
    validate: {
      title: (value: string) =>
        value && value.length < 2 ? t('common.errors.tooShort', 'Trop court') : null,
    },
  });

  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  useEffect(() => {
    const populateForm = async () => {
      if (project) {
        form.initialize({
          title: project.title,
          description: parseDescription(project.description),
          status: project.status,
          estimatedCost: project.estimatedCost ?? undefined,
          estimatedDuration: project.estimatedDuration ?? undefined,
          estimatedDurationUnit: project.estimatedDurationUnit ?? undefined,
          deadline: project.deadline ? dayjs(project.deadline).toDate() : null,
          completionDate: project.completionDate ? dayjs(project.completionDate).toDate() : null,
          label: project.label ?? null,
          priority: project.priority ?? null,
          memberIds: project.members?.map(m => m.id) ?? [],
          participantIds: project.participants?.map(p => p.id) ?? [],
          attachments: [],
        });

        type ExtendedProjectDto = ProjectDto & {
          attachments?: { id: string; name: string }[];
        };
        const extendedProject = project as unknown as ExtendedProjectDto;

        if (extendedProject.attachments && extendedProject.attachments.length > 0) {
          try {
            const attachmentInfoPromises = extendedProject.attachments.map(attachment =>
              fetchFileInfo(attachment.id)
            );

            const resolvedFileInfos = await Promise.all(attachmentInfoPromises);

            const formAttachments = extendedProject.attachments
              .map((attachment): FormAttachment | undefined => {
                const fileInfo = resolvedFileInfos.find(info => info.id === attachment.id);
                if (!fileInfo) return undefined;
                return {
                  fileId: attachment.id,
                  name: attachment.name,
                  previewUrl: fileInfo?.urls?.previewThumbnailUrl,
                  type: fileInfo.type as unknown as AssetStorageItemDtoTypeEnum,
                  video: fileInfo.video,
                };
              })
              .filter((attachment): attachment is FormAttachment => attachment !== undefined);

            form.setFieldValue('attachments', formAttachments);
          } catch (error) {
            console.error('Failed to fetch attachment information:', error);
          }
        }
      }
    };

    populateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const handleSubmit = (values: ProjectFormValues) => {
    if (!projectId) return;
    const { attachments, deadline, completionDate, label, priority, description, ...rest } = values;
    type ExtendedUpdateProjectDto = UpdateProjectDto & { attachmentIds: string[] };
    const payload = {
      ...rest,
      description: isDescriptionEmpty(description) ? undefined : JSON.stringify(description),
      deadline: deadline ?? undefined,
      completionDate: completionDate ?? undefined,
      label: label === null ? null : label,
      priority: priority === null ? null : priority,
      attachmentIds: attachments.map(a => a.fileId).filter(Boolean) as string[],
    } as unknown as ExtendedUpdateProjectDto;

    updateProject(
      { projectId, data: payload },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handleDelete = () => {
    if (!projectId) return;
    if (
      window.confirm(t('common.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce projet ?'))
    ) {
      deleteProject(projectId, {
        onSuccess: () => onClose(),
      });
    }
  };

  if (isLoadingProjects) return <LoadingOverlay visible />;

  return (
    <ProjectForm
      form={form}
      onSubmit={form.onSubmit(handleSubmit)}
      onCancel={onClose}
      onDelete={handleDelete}
      isLoading={isUpdating}
      isDeleting={isDeleting}
      isEditing
    />
  );
};

export default ProjectEditionFormWrapper;
